import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Plus, Trash2, Edit2, Save, X, Loader2, GraduationCap, BookOpen,
  Users, ChevronDown, ChevronRight, Play, CheckCircle, Eye, UserPlus,
  BarChart3, Video, HelpCircle, FileText, ArrowLeft
} from 'lucide-react';

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'bible_study', label: 'Bible Study' },
  { value: 'academic', label: 'Academic' },
  { value: 'safety', label: 'Safety' },
  { value: 'language', label: 'Language' },
  { value: 'arts', label: 'Arts & Music' },
  { value: 'science', label: 'Science' },
  { value: 'math', label: 'Mathematics' },
  { value: 'reading', label: 'Reading' },
  { value: 'custom', label: 'Custom' },
];

const AGE_GROUPS = [
  { value: 'all', label: 'All Ages' },
  { value: 'seeds', label: 'Seeds (3-5)' },
  { value: 'roots', label: 'Roots (6-9)' },
  { value: 'warriors', label: 'Warriors (10-13)' },
];

const categoryColor = (cat) => {
  const colors = {
    bible_study: 'bg-purple-100 text-purple-700',
    academic: 'bg-blue-100 text-blue-700',
    safety: 'bg-red-100 text-red-700',
    language: 'bg-green-100 text-green-700',
    arts: 'bg-pink-100 text-pink-700',
    science: 'bg-cyan-100 text-cyan-700',
    math: 'bg-amber-100 text-amber-700',
    reading: 'bg-indigo-100 text-indigo-700',
  };
  return colors[cat] || 'bg-slate-100 text-slate-700';
};

// ==================== LESSON EDITOR ====================
const LessonEditor = ({ lesson, index, onChange, onRemove }) => {
  const [expanded, setExpanded] = useState(true);
  const [questions, setQuestions] = useState(lesson.questions || []);

  const updateField = (field, value) => {
    onChange({ ...lesson, [field]: value });
  };

  const addQuestion = () => {
    const newQ = { question: '', question_type: 'multiple_choice', options: ['', '', '', ''], correct_answer: '', points: 10 };
    const updated = [...questions, newQ];
    setQuestions(updated);
    onChange({ ...lesson, questions: updated });
  };

  const updateQuestion = (qi, field, value) => {
    const updated = [...questions];
    updated[qi] = { ...updated[qi], [field]: value };
    setQuestions(updated);
    onChange({ ...lesson, questions: updated });
  };

  const updateOption = (qi, oi, value) => {
    const updated = [...questions];
    updated[qi].options[oi] = value;
    setQuestions(updated);
    onChange({ ...lesson, questions: updated });
  };

  const removeQuestion = (qi) => {
    const updated = questions.filter((_, i) => i !== qi);
    setQuestions(updated);
    onChange({ ...lesson, questions: updated });
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid={`lesson-editor-${index}`}>
      <div className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors cursor-pointer"
        onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          <span className="text-xs font-semibold text-slate-400">LESSON {index + 1}</span>
          <span className="text-sm font-medium text-slate-700 truncate max-w-[200px]">{lesson.title || 'Untitled'}</span>
          {questions.length > 0 && <Badge variant="outline" className="text-[10px]"><HelpCircle className="w-3 h-3 mr-1" />{questions.length} Q</Badge>}
          {lesson.video_url && <Badge variant="outline" className="text-[10px]"><Video className="w-3 h-3 mr-1" />Video</Badge>}
        </div>
        <button className="text-red-400 hover:text-red-600 h-7 px-2 rounded" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {expanded && (
        <div className="p-3 pt-0 space-y-3 border-t border-slate-100">
          <div>
            <Label className="text-xs">Lesson Title</Label>
            <Input value={lesson.title} onChange={e => updateField('title', e.target.value)} placeholder="e.g., Introduction to Creation" className="mt-1" data-testid={`lesson-title-${index}`} />
          </div>
          <div>
            <Label className="text-xs">Content</Label>
            <Textarea value={lesson.content} onChange={e => updateField('content', e.target.value)} placeholder="Lesson content... supports plain text" className="mt-1 min-h-[80px]" data-testid={`lesson-content-${index}`} />
          </div>
          <div>
            <Label className="text-xs">Video URL (optional)</Label>
            <Input value={lesson.video_url || ''} onChange={e => updateField('video_url', e.target.value)} placeholder="https://youtube.com/watch?v=..." className="mt-1" data-testid={`lesson-video-${index}`} />
          </div>

          {/* Quiz Questions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold">Quiz Questions</Label>
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={addQuestion} data-testid={`add-question-${index}`}>
                <Plus className="w-3 h-3 mr-1" /> Add Question
              </Button>
            </div>
            {questions.map((q, qi) => (
              <div key={qi} className="bg-slate-50 rounded-md p-3 mb-2 space-y-2" data-testid={`question-${index}-${qi}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-slate-400">Q{qi + 1}</span>
                  <div className="flex items-center gap-2">
                    <Select value={q.question_type} onValueChange={v => updateQuestion(qi, 'question_type', v)}>
                      <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                        <SelectItem value="true_false">True/False</SelectItem>
                        <SelectItem value="short_answer">Short Answer</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" className="h-7 text-red-400" onClick={() => removeQuestion(qi)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
                <Input value={q.question} onChange={e => updateQuestion(qi, 'question', e.target.value)} placeholder="Question text" className="text-sm" />
                {q.question_type === 'multiple_choice' && (
                  <div className="grid grid-cols-2 gap-1.5">
                    {(q.options || ['', '', '', '']).map((opt, oi) => (
                      <Input key={oi} value={opt} onChange={e => updateOption(qi, oi, e.target.value)}
                        placeholder={`Option ${String.fromCharCode(65 + oi)}`} className="text-xs" />
                    ))}
                  </div>
                )}
                {q.question_type === 'true_false' && (
                  <div className="flex gap-2">
                    <Badge variant={q.correct_answer === 'true' ? 'default' : 'outline'} className="cursor-pointer" onClick={() => updateQuestion(qi, 'correct_answer', 'true')}>True</Badge>
                    <Badge variant={q.correct_answer === 'false' ? 'default' : 'outline'} className="cursor-pointer" onClick={() => updateQuestion(qi, 'correct_answer', 'false')}>False</Badge>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Label className="text-[10px]">Correct Answer:</Label>
                  <Input value={q.correct_answer} onChange={e => updateQuestion(qi, 'correct_answer', e.target.value)}
                    placeholder={q.question_type === 'multiple_choice' ? 'e.g., Option A text' : 'Answer'} className="text-xs flex-1" />
                  <Label className="text-[10px]">Pts:</Label>
                  <Input type="number" value={q.points} onChange={e => updateQuestion(qi, 'points', parseInt(e.target.value) || 0)} className="text-xs w-16" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== CURRICULUM BUILDER ====================
const CurriculumBuilder = ({ api, editData, onSave, onCancel }) => {
  const [title, setTitle] = useState(editData?.title || '');
  const [description, setDescription] = useState(editData?.description || '');
  const [category, setCategory] = useState(editData?.category || 'general');
  const [ageGroup, setAgeGroup] = useState(editData?.age_group || 'all');
  const [lessons, setLessons] = useState(editData?.lessons?.map(l => ({ ...l })) || []);
  const [saving, setSaving] = useState(false);

  const addLesson = () => {
    setLessons([...lessons, { title: '', content: '', video_url: '', questions: [], order: lessons.length }]);
  };

  const updateLesson = (index, updated) => {
    const newLessons = [...lessons];
    newLessons[index] = updated;
    setLessons(newLessons);
  };

  const removeLesson = (index) => {
    setLessons(lessons.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        title, description, category, age_group: ageGroup,
        lessons: lessons.map((l, i) => ({ ...l, order: i })),
      };
      if (editData?.id) {
        await api.put(`/org-admin/curricula/${editData.id}`, payload);
        toast.success('Curriculum updated');
      } else {
        await api.post('/org-admin/curricula', payload);
        toast.success('Curriculum created');
      }
      onSave();
    } catch (err) {
      toast.error('Failed to save: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="curriculum-builder">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onCancel} className="gap-2" data-testid="curriculum-builder-back">
          <ArrowLeft className="w-4 h-4" /> Back to List
        </Button>
        <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="curriculum-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {editData?.id ? 'Update Curriculum' : 'Create Curriculum'}
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Curriculum Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Introduction to Bible History" className="mt-1" data-testid="curriculum-title-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe what this curriculum covers..." className="mt-1" data-testid="curriculum-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1" data-testid="curriculum-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Age Group</Label>
              <Select value={ageGroup} onValueChange={setAgeGroup}>
                <SelectTrigger className="mt-1" data-testid="curriculum-age-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AGE_GROUPS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lessons */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Lessons ({lessons.length})</CardTitle>
            <Button onClick={addLesson} size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-lesson-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Lesson
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {lessons.length === 0 && (
            <div className="text-center py-8 text-slate-400">
              <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No lessons yet. Click "Add Lesson" to get started.</p>
            </div>
          )}
          {lessons.map((lesson, i) => (
            <LessonEditor key={i} lesson={lesson} index={i} onChange={l => updateLesson(i, l)} onRemove={() => removeLesson(i)} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== ASSIGN DIALOG ====================
const AssignDialog = ({ api, open, onClose, curriculum, children, classes }) => {
  const [assignType, setAssignType] = useState('child');
  const [selectedId, setSelectedId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assigning, setAssigning] = useState(false);

  const handleAssign = async () => {
    if (!selectedId) { toast.error('Please select a child or group'); return; }
    setAssigning(true);
    try {
      const res = await api.post('/org-admin/curricula/assign', {
        curriculum_id: curriculum.id,
        assign_to_type: assignType,
        assign_to_id: selectedId,
        due_date: dueDate || null,
      });
      toast.success(`Assigned to ${res.data.assigned} student(s)`);
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent data-testid="assign-curriculum-dialog">
        <DialogHeader>
          <DialogTitle>Assign: {curriculum?.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex gap-2">
            <Button variant={assignType === 'child' ? 'default' : 'outline'} size="sm" onClick={() => { setAssignType('child'); setSelectedId(''); }} data-testid="assign-type-child">
              Individual Child
            </Button>
            <Button variant={assignType === 'group' ? 'default' : 'outline'} size="sm" onClick={() => { setAssignType('group'); setSelectedId(''); }} data-testid="assign-type-group">
              Class / Group
            </Button>
          </div>
          <div>
            <Label>{assignType === 'child' ? 'Select Child' : 'Select Class'}</Label>
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger className="mt-1" data-testid="assign-select"><SelectValue placeholder={`Choose a ${assignType === 'child' ? 'child' : 'class'}...`} /></SelectTrigger>
              <SelectContent>
                {assignType === 'child'
                  ? (children || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)
                  : (classes || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)
                }
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Due Date (optional)</Label>
            <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="mt-1" data-testid="assign-due-date" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
          <Button onClick={handleAssign} disabled={assigning} className="bg-indigo-600 hover:bg-indigo-700" data-testid="assign-confirm-btn">
            {assigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== PROGRESS VIEW ====================
const ProgressView = ({ api, curriculum, onBack }) => {
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/org-admin/curricula/${curriculum.id}/progress`).then(r => {
      setProgress(r.data.progress || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [curriculum.id]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-4" data-testid="progress-view">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={onBack} data-testid="progress-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div>
          <h2 className="text-lg font-bold text-slate-800">{curriculum.title}</h2>
          <p className="text-xs text-slate-500">{progress.length} students assigned</p>
        </div>
      </div>

      {progress.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-slate-400"><Users className="w-10 h-10 mx-auto mb-2 opacity-40" /><p>No students assigned yet.</p></CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Lessons</TableHead>
                  <TableHead>Points</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {progress.map((p, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{p.child_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{
                            width: `${p.progress_percent}%`,
                            backgroundColor: p.progress_percent >= 100 ? '#10b981' : p.progress_percent > 50 ? '#3b82f6' : '#f59e0b'
                          }} />
                        </div>
                        <span className="text-xs font-medium text-slate-600">{p.progress_percent}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{p.completed_lessons.length}/{curriculum.lesson_count || curriculum.lessons?.length || 0}</TableCell>
                    <TableCell className="text-sm font-medium">{p.total_points}</TableCell>
                    <TableCell className="text-xs text-slate-500">{p.last_activity ? new Date(p.last_activity).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>
                      {p.completed_at ? <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>
                        : p.progress_percent > 0 ? <Badge className="bg-blue-100 text-blue-700">In Progress</Badge>
                        : <Badge variant="outline">Not Started</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== MAIN SECTION ====================
const CustomCurriculumSection = ({ api }) => {
  const [curricula, setCurricula] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [editData, setEditData] = useState(null);
  const [selectedCurriculum, setSelectedCurriculum] = useState(null);
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [children, setChildren] = useState([]);
  const [classes, setClasses] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const fetchCurricula = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (filterCategory) params.append('category', filterCategory);
      const res = await api.get(`/org-admin/curricula?${params.toString()}`);
      setCurricula(res.data.curricula || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChildrenAndClasses = async () => {
    try {
      const [childRes, classRes] = await Promise.all([
        api.get('/org-admin/students').catch(() => ({ data: { students: [] } })),
        api.get('/org-admin/classes').catch(() => ({ data: { classes: [] } })),
      ]);
      setChildren(childRes.data?.students || childRes.data || []);
      setClasses(classRes.data?.classes || classRes.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchCurricula(); fetchChildrenAndClasses(); }, []);
  useEffect(() => { fetchCurricula(); }, [searchQuery, filterCategory]);

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/org-admin/curricula/${id}`);
      toast.success('Curriculum deleted');
      fetchCurricula();
    } catch (err) {
      toast.error('Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const handlePublish = async (curriculum) => {
    try {
      const newStatus = curriculum.status === 'published' ? 'draft' : 'published';
      await api.put(`/org-admin/curricula/${curriculum.id}`, { status: newStatus });
      toast.success(newStatus === 'published' ? 'Curriculum published' : 'Curriculum set to draft');
      fetchCurricula();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  if (view === 'builder') {
    return <CurriculumBuilder api={api} editData={editData} onSave={() => { setView('list'); setEditData(null); fetchCurricula(); }} onCancel={() => { setView('list'); setEditData(null); }} />;
  }

  if (view === 'progress' && selectedCurriculum) {
    return <ProgressView api={api} curriculum={selectedCurriculum} onBack={() => { setView('list'); setSelectedCurriculum(null); }} />;
  }

  return (
    <div className="space-y-5" data-testid="custom-curriculum-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <GraduationCap className="w-7 h-7 text-indigo-500" /> Custom Curriculum
          </h1>
          <p className="text-slate-500 text-sm mt-1">Create, manage, and assign custom curricula to students</p>
        </div>
        <Button onClick={() => { setEditData(null); setView('builder'); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-curriculum-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Curriculum
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-50 to-white">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500">Total Curricula</p>
            <p className="text-2xl font-bold text-indigo-700" data-testid="stat-total">{curricula.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500">Published</p>
            <p className="text-2xl font-bold text-emerald-700" data-testid="stat-published">{curricula.filter(c => c.status === 'published').length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-white">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500">Drafts</p>
            <p className="text-2xl font-bold text-amber-700" data-testid="stat-drafts">{curricula.filter(c => c.status !== 'published').length}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500">Total Lessons</p>
            <p className="text-2xl font-bold text-blue-700" data-testid="stat-lessons">{curricula.reduce((s, c) => s + (c.lesson_count || 0), 0)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <Input placeholder="Search curricula..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="max-w-xs" data-testid="curriculum-search" />
        <Select value={filterCategory} onValueChange={v => setFilterCategory(v === 'all_cats' ? '' : v)}>
          <SelectTrigger className="w-44" data-testid="curriculum-filter-category"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all_cats">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Curriculum List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
      ) : curricula.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500 text-sm">No curricula yet. Create your first one!</p>
            <Button onClick={() => { setEditData(null); setView('builder'); }} className="mt-4 bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" /> Create Curriculum
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {curricula.map(c => (
            <Card key={c.id} className="hover:shadow-md transition-shadow" data-testid={`curriculum-card-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-800 truncate">{c.title}</h3>
                      <Badge className={cn('text-[10px]', categoryColor(c.category))}>{CATEGORIES.find(cat => cat.value === c.category)?.label || c.category}</Badge>
                      <Badge className={cn('text-[10px]', c.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600')}>
                        {c.status === 'published' ? 'Published' : 'Draft'}
                      </Badge>
                      {c.age_group !== 'all' && <Badge variant="outline" className="text-[10px]">{AGE_GROUPS.find(a => a.value === c.age_group)?.label}</Badge>}
                    </div>
                    {c.description && <p className="text-xs text-slate-500 mb-2 line-clamp-1">{c.description}</p>}
                    <div className="flex items-center gap-4 text-xs text-slate-400">
                      <span><BookOpen className="w-3 h-3 inline mr-1" />{c.lesson_count || 0} lessons</span>
                      <span><Users className="w-3 h-3 inline mr-1" />{c.assignment_count || 0} assigned</span>
                      <span>Created {new Date(c.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 ml-3">
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setSelectedCurriculum(c); setView('progress'); }} data-testid={`view-progress-${c.id}`}>
                      <BarChart3 className="w-3.5 h-3.5 mr-1" /> Progress
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setAssignTarget(c); setAssignDialog(true); }} data-testid={`assign-btn-${c.id}`}>
                      <UserPlus className="w-3.5 h-3.5 mr-1" /> Assign
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handlePublish(c)} data-testid={`publish-btn-${c.id}`}>
                      {c.status === 'published' ? 'Unpublish' : 'Publish'}
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setEditData(c); setView('builder'); }} data-testid={`edit-btn-${c.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 text-xs text-red-500 hover:text-red-700" disabled={deleting === c.id} onClick={() => handleDelete(c.id)} data-testid={`delete-btn-${c.id}`}>
                      {deleting === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Assign Dialog */}
      {assignTarget && (
        <AssignDialog api={api} open={assignDialog} onClose={(refresh) => { setAssignDialog(false); setAssignTarget(null); if (refresh) fetchCurricula(); }}
          curriculum={assignTarget} children={children} classes={classes} />
      )}
    </div>
  );
};

export default CustomCurriculumSection;
